import type { BrokerCapabilities } from "../services/brokerIntegrationApi";

export type BinanceHistoryPath = "recent_only" | "needs_csv_for_history";

export type BinanceTradeSyncStepAction = "api_sync" | "csv_import";

export interface BinanceTradeSyncStep {
  action: BinanceTradeSyncStepAction;
  label: string;
  covers?: string;
  covers_from_date?: string;
  covers_to_date?: string;
  endpoint?: string;
}

export interface BinanceTradeSyncInfo {
  strategy: string;
  apiSyncLookbackDays: number;
  apiSyncFromDate: string;
  apiSyncToDate: string;
  historyBeforeApiRequiresCsvImport: boolean;
  csvImportEndpoint: string;
  optionalAccountStartDateField: string;
  accountStartDatePurpose: string;
  summary: string;
  steps: BinanceTradeSyncStep[];
}

const HISTORY_PATH_STORAGE_KEY = "tessera.binance_trade_history_path";

function formatYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function defaultLookbackDates(lookbackDays: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - lookbackDays);
  return { from: formatYmd(from), to: formatYmd(to) };
}

function parseSteps(raw: unknown, from: string, to: string): BinanceTradeSyncStep[] {
  if (!Array.isArray(raw)) {
    return [
      {
        action: "api_sync",
        label: "Sync recent trades",
        covers_from_date: from,
        covers_to_date: to,
      },
      {
        action: "csv_import",
        label: "Import order history CSV",
        covers: `Trades before ${from}`,
      },
    ];
  }
  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const o = item as Record<string, unknown>;
      const action = o.action;
      if (action !== "api_sync" && action !== "csv_import") return null;
      return {
        action,
        label: String(o.label || (action === "api_sync" ? "Sync recent trades" : "Import order history CSV")),
        covers: o.covers != null ? String(o.covers) : undefined,
        covers_from_date: o.covers_from_date != null ? String(o.covers_from_date).slice(0, 10) : undefined,
        covers_to_date: o.covers_to_date != null ? String(o.covers_to_date).slice(0, 10) : undefined,
        endpoint: o.endpoint != null ? String(o.endpoint) : undefined,
      };
    })
    .filter(Boolean) as BinanceTradeSyncStep[];
}

/** Read `trade_sync` from GET /broker/capabilities (Binance). */
export function resolveBinanceTradeSync(
  cap: BrokerCapabilities | null | undefined
): BinanceTradeSyncInfo | null {
  if (!cap || String(cap.broker_name || "").toLowerCase() !== "binance") return null;
  const raw = (cap as { trade_sync?: Record<string, unknown> }).trade_sync;
  if (!raw || typeof raw !== "object") return null;

  const lookbackDays =
    typeof raw.api_sync_lookback_days === "number" && raw.api_sync_lookback_days > 0
      ? raw.api_sync_lookback_days
      : 30;
  const fallback = defaultLookbackDates(lookbackDays);
  const from =
    raw.api_sync_from_date != null
      ? String(raw.api_sync_from_date).slice(0, 10)
      : fallback.from;
  const to =
    raw.api_sync_to_date != null ? String(raw.api_sync_to_date).slice(0, 10) : fallback.to;

  const guidance =
    raw.user_guidance && typeof raw.user_guidance === "object"
      ? (raw.user_guidance as Record<string, unknown>)
      : {};

  const summary =
    typeof guidance.summary === "string" && guidance.summary.trim()
      ? guidance.summary.trim()
      : `API sync covers Spot fills from about ${from} through ${to}. For older trades, import a Binance Spot order history CSV once.`;

  return {
    strategy: String(raw.strategy || "my_trades_from_id"),
    apiSyncLookbackDays: lookbackDays,
    apiSyncFromDate: from,
    apiSyncToDate: to,
    historyBeforeApiRequiresCsvImport: Boolean(raw.history_before_api_requires_csv_import),
    csvImportEndpoint: String(raw.csv_import_endpoint || "/api/broker/binance/csv-import"),
    optionalAccountStartDateField: String(
      raw.optional_account_start_date_field || "binance_account_start_date"
    ),
    accountStartDatePurpose: String(
      raw.account_start_date_purpose || "portfolio_analytics_inception"
    ),
    summary,
    steps: parseSteps(guidance.steps, from, to),
  };
}

export function loadBinanceHistoryPath(): BinanceHistoryPath | null {
  if (typeof window === "undefined") return null;
  const v = window.sessionStorage.getItem(HISTORY_PATH_STORAGE_KEY);
  if (v === "recent_only" || v === "needs_csv_for_history") return v;
  return null;
}

export function saveBinanceHistoryPath(path: BinanceHistoryPath): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(HISTORY_PATH_STORAGE_KEY, path);
}

export function clearBinanceHistoryPath(): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(HISTORY_PATH_STORAGE_KEY);
}

export function formatBinanceSyncDateRange(from: string, to: string): string {
  try {
    const f = new Date(`${from}T12:00:00`);
    const t = new Date(`${to}T12:00:00`);
    if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return `${from} – ${to}`;
    const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
    return `${f.toLocaleDateString(undefined, opts)} – ${t.toLocaleDateString(undefined, opts)}`;
  } catch {
    return `${from} – ${to}`;
  }
}
